/**
 * useClinicRules Hook - AWS API Version
 *
 * Manages clinic rule configuration via the AWS API layer.
 * Merges in default rules for any that are missing from the backend,
 * ensuring the full canonical set is always available in the UI.
 */

import { useState, useCallback, useEffect } from 'react';
import { api } from '../lib/api';
import { DEFAULT_SPECIALTY_DRUGS } from '../utils/drugWatchlist';

/**
 * Canonical set of default rules. If any are missing from the API response,
 * they are merged in client-side so the UI always shows the full set.
 * The backend will also auto-create them on next deploy (see clinic.js DEFAULT_RULES).
 */
const DEFAULT_RULES = [
  {
    code: 'escalate_no_response',
    name: 'Escalate No Response',
    description: 'Move patients back into the follow-up queue if they haven\'t responded after multiple outreach attempts.',
    category: 'risk',
    enabled: true,
    config: { attempts: 3 },
    defaultConfig: { attempts: 3 },
  },
  {
    code: 'missed_visits_flag',
    name: 'Flag High Missed Visits',
    description: 'Prioritize patients who have missed multiple appointments and may be at risk of disengaging from care.',
    category: 'risk',
    enabled: true,
    config: { threshold: 3 },
    defaultConfig: { threshold: 3 },
  },
  {
    code: 'flag_urgent_status',
    name: 'Flag Urgent Status',
    description: 'Automatically surface patients marked as urgent so they receive timely follow-up.',
    category: 'risk',
    enabled: true,
    config: {},
    defaultConfig: {},
  },
  {
    code: 'flag_concerning_status',
    name: 'Flag Concerning Status',
    description: 'Prioritize patients whose status indicates they need attention, such as "callback", "no show", or "overdue".',
    category: 'risk',
    enabled: true,
    config: {},
    defaultConfig: {},
  },
  {
    code: 'overdue_appointment',
    name: 'Overdue Appointment',
    description: 'Flag patients who haven\'t been seen in a while based on days since their last visit.',
    category: 'risk',
    enabled: true,
    config: { high_days: 90, medium_days: 30 },
    defaultConfig: { high_days: 90, medium_days: 30 },
  },
  {
    code: 'med_adherence_risk',
    name: 'Medication Adherence Risk',
    description: 'Flag patients on Medicare with specialty drugs who are above the configured age threshold.',
    category: 'risk',
    enabled: true,
    config: { age_threshold: 65, drug_list: [...DEFAULT_SPECIALTY_DRUGS] },
    defaultConfig: { age_threshold: 65, drug_list: [...DEFAULT_SPECIALTY_DRUGS] },
  },
  {
    code: 'business_hours_only',
    name: 'Business Hours Only',
    description: 'Send automated messages only during approved business hours.',
    category: 'campaign',
    enabled: true,
    config: { start_hour: 9, end_hour: 17, timezone: 'America/New_York' },
    defaultConfig: { start_hour: 9, end_hour: 17, timezone: 'America/New_York' },
  },
  {
    code: 'max_messages_per_week',
    name: 'Message Frequency Limit',
    description: 'Limit how many messages a patient can receive in a given time period.',
    category: 'campaign',
    enabled: true,
    config: { max_count: 3 },
    defaultConfig: { max_count: 3 },
  },
  {
    code: 'min_days_between_messages',
    name: 'Minimum Days Between Messages',
    description: 'Require a minimum number of days between messages sent to the same patient.',
    category: 'campaign',
    enabled: true,
    config: { days: 1 },
    defaultConfig: { days: 1 },
  },
];

export const useClinicRules = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.getClinicRules();
      const data = response.rules || response || [];

      // Transform to flat structure for easier UI consumption
      const transformed = data.map(r => {
        const code = r.rule_code || r.code;
        const defaultRule = DEFAULT_RULES.find(d => d.code === code);
        return {
          id: r.id || code,
          code,
          name: r.name || r.rule_name || (defaultRule && defaultRule.name),
          description: r.description || (defaultRule && defaultRule.description),
          category: r.category || (defaultRule && defaultRule.category) || null,
          enabled: r.enabled,
          config: r.config,
          defaultConfig: r.default_config || r.defaultConfig || (defaultRule && defaultRule.defaultConfig),
          updatedAt: r.updated_at || r.updatedAt,
        };
      });

      // Merge in any missing defaults so the UI always shows the full set
      const existingCodes = new Set(transformed.map(r => r.code));
      const missing = DEFAULT_RULES
        .filter(d => !existingCodes.has(d.code))
        .map(d => ({ ...d, id: d.code }));

      const merged = [...transformed, ...missing];

      // Sort by category (risk first) then by name
      merged.sort((a, b) => {
        if (a.category !== b.category) {
          return a.category === 'risk' ? -1 : 1;
        }
        return (a.name || '').localeCompare(b.name || '');
      });

      setRules(merged);
    } catch (err) {
      console.error('Failed to fetch clinic rules:', err);
      setError(err.message);
      // Fall back to defaults so the UI always shows the full rule set
      setRules(DEFAULT_RULES.map(d => ({ ...d, id: d.code })));
    } finally {
      setLoading(false);
    }
  }, []);

  const updateRule = useCallback(async (ruleId, updates) => {
    if (!ruleId || !updates || Object.keys(updates).length === 0) {
      return { success: false, error: new Error('Invalid update payload') };
    }

    try {
      await api.updateClinicRule(ruleId, updates);

      // Optimistically update local state
      setRules(prev =>
        prev.map(r => (r.id === ruleId ? { ...r, ...updates } : r))
      );

      return { success: true };
    } catch (error) {
      console.error(`Failed to update rule ${ruleId}:`, error);
      return { success: false, error };
    }
  }, []);

  const resetRuleToDefault = useCallback(async (ruleId) => {
    const rule = rules.find(r => r.id === ruleId);
    if (!rule) {
      return { success: false, error: new Error('Rule not found') };
    }

    return updateRule(ruleId, { config: rule.defaultConfig });
  }, [rules, updateRule]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  // Helper to get rules by category
  const getRulesByCategory = useCallback((category) => {
    return rules.filter(r => r.category === category);
  }, [rules]);

  // Helper to get a specific rule by code
  const getRuleByCode = useCallback((code) => {
    return rules.find(r => r.code === code);
  }, [rules]);

  return {
    rules,
    loading,
    error,
    refetch: fetchRules,
    updateRule,
    resetRuleToDefault,
    getRulesByCategory,
    getRuleByCode,
  };
};
